import React, {useState} from 'react';
import PropTypes from 'prop-types';
import '../App.css';
import {useNavigate} from "react-router-dom";
import ReactTooltip from "react-tooltip";
import usePersistedState from './usePersistedState';
import {SpinnerCircular} from 'spinners-react';

const initialValues = {
    age: '',
    weight: '',
    height: '',

    phases: '',
    length: '',
    width: '',
    numbAneu: '',
    vessel: '',
    anomalies: '',
    gender: 'male',
    shape: 'unknown',
    hypertension: 'unknown',
    anticoagulants: 'unknown',
    nicotine: 'unknown',
    diabetes: 'unknown'

}

const InputForm = ({sendDataToParent}) => {
    const [state, setState] = usePersistedState('state', initialValues);
    const [loading, setLoading] = useState(false)

    const navigate = useNavigate();
    const postToApi = async () => {
        setLoading(true);

        // POST request using fetch
        const data = {
            'Alter': parseFloat(state.age),
            'Geschlecht': state.gender,
            'Körpergewicht': parseFloat(state.weight),
            'Körpergrösse': parseFloat(state.height),
            'Bluthochdruck': state.hypertension,
            'Blutverdünner': state.anticoagulants,
            'Nikotin': state.nicotine,
            'Diabetesmellitus': state.diabetes,

            'PHASES': parseFloat(state.phases),
            'SABAnsLänge': parseFloat(state.length),
            'SABAnsBreite': parseFloat(state.width),
            'AnzahlAneu': parseFloat(state.numbAneu),
            'Form': state.shape,
            'TrägerGefäss': state.vessel,
            'Anomalien': state.anomalies,
        }
        const requestOptions = {
            method: 'POST', headers: {
                'Content-Type': 'application/json'
            }, body: JSON.stringify(data)
        };
        await fetch(process.env.PUBLIC_URL + '/api/diagnosis', requestOptions)
            .then(response => response.json())
            .then(response => {
                sendDataToParent({
                    'probability': response.probability.toString(),
                    'calibrated_probability': response.calibrated_probability.toString()
                });
            })
        setLoading(false)
        navigate('/diagnosis')
    };

    const handleKeyPressed = e => {
        if (!/[0-9]/.test(e.key)) {
            e.preventDefault();
        }
    }
    const onInputChange = e => {
        const {name, value} = e.target
        setState(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    return (<div className="main-content">
        <h1>Predict rupture risk of intracranial aneurysm</h1>
        <p>Enter the following values:</p>
        <div className="rowC">
            <div className="patient">
                <h3>Patient-specific features:</h3>
                <div className="form-group">
                    <label htmlFor="age">Age:</label>
                    <input className="input-control" name="age" value={state.age} type="number" min="0" max="150"
                           placeholder="Age in years" onKeyPress={handleKeyPressed} onChange={onInputChange}/>
                </div>
                <div className="form-group">
                    <label>Gender:</label>
                    <label className="radio">
                        <input name="gender" value="male" type="radio" checked={state.gender === "male"}
                               onChange={onInputChange} required/>
                        Male</label>
                    <label className="radio">
                        <input name="gender" value="female" type="radio" checked={state.gender === "female"}
                               onChange={onInputChange} required/>
                        Female</label>
                </div>
                <div className="form-group">
                    <label htmlFor="weight">Body weight [kg]:</label>
                    <input className="input-control" name="weight" value={state.weight} type="number" min="0" max="300"
                           placeholder="Body weight in kg" onKeyPress={handleKeyPressed} onChange={onInputChange}/>
                </div>
                <div className="form-group">
                    <label htmlFor="height">Body height [cm]:</label>
                    <input className="input-control" name="height" value={state.height} type="number" min="0" max="300"
                           placeholder="Body height in cm" onKeyPress={handleKeyPressed} onChange={onInputChange}/>
                </div>

                <div className="form-group">
                    <label>Hypertension:</label>
                    <label className="radio">
                        <input name="hypertension" value="unknown" type="radio"
                               checked={state.hypertension === "unknown"}
                               onChange={onInputChange}/>Unknown
                    </label>
                    <label className="radio">
                        <input name="hypertension" value="yes" type="radio"
                               checked={state.hypertension === 'yes'}
                               onChange={onInputChange}/>Yes
                    </label>
                    <label className="radio">
                        <input name="hypertension" value="no" type="radio" checked={state.hypertension === 'no'}
                               onChange={onInputChange}/>No
                    </label>
                </div>

                <div className="form-group">
                    <label>Anticoagulants:</label>
                    <label className="radio">
                        <input name="anticoagulants" value="unknown" type="radio"
                               checked={state.anticoagulants === "unknown"}
                               onChange={onInputChange}/>Unknown
                    </label>
                    <label className="radio">
                        <input name="anticoagulants" value="yes" type="radio"
                               checked={state.anticoagulants === 'yes'}
                               onChange={onInputChange}/>Yes
                    </label>
                    <label className="radio">
                        <input name="anticoagulants" value="no" type="radio"
                               checked={state.anticoagulants === 'no'}
                               onChange={onInputChange}/>No
                    </label>
                </div>

                <div className="form-group">
                    <label>Nicotine:</label>
                    <label className="radio">
                        <input name="nicotine" value="unknown" type="radio"
                               checked={state.nicotine === "unknown"}
                               onChange={onInputChange}/>Unknown
                    </label>
                    <label className="radio">
                        <input name="nicotine" value="yes" type="radio" checked={state.nicotine === 'yes'}
                               onChange={onInputChange}/>Yes
                    </label>
                    <label className="radio">
                        <input name="nicotine" value="no" type="radio" checked={state.nicotine === 'no'}
                               onChange={onInputChange}/>No
                    </label>
                </div>

                <div className="form-group">
                    <label>Diabetes mellitus:</label>
                    <label className="radio">
                        <input name="diabetes" value="unknown" type="radio"
                               checked={state.diabetes === "unknown"}
                               onChange={onInputChange}/>Unknown
                    </label>
                    <label className="radio">
                        <input name="diabetes" value="yes" type="radio" checked={state.diabetes === 'yes'}
                               onChange={onInputChange}/>Yes
                    </label>
                    <label className="radio">
                        <input name="diabetes" value="no" type="radio" checked={state.diabetes === 'no'}
                               onChange={onInputChange}/>No
                    </label>
                </div>
            </div>

            <div className="aneurysm">
                <h3>Aneurysm-specific features:</h3>
                <div className="form-group">
                    <label htmlFor="phases">PHASES score:</label>
                    <input className="input-control" name="phases" value={state.phases} type="number" min="0" max="22"
                           placeholder="PHASES score" onKeyPress={handleKeyPressed} onChange={onInputChange}/><br/>
                </div>
                <div className="form-group">
                    <label>Size:</label>
                    <label className="right-inline" htmlFor="length">Length [mm]:</label>
                    <input className="input-control" type="number" name="length" value={state.length} placeholder="Length in mm"
                           onKeyPress={handleKeyPressed} onChange={onInputChange}/>

                    <label className="right-inline" htmlFor="width">Width [mm]:</label>
                    <input className="input-control" type="number" name="width" value={state.width} placeholder="Width in mm"
                           onKeyPress={handleKeyPressed} onChange={onInputChange}/>
                </div>
                <div className="form-group">
                    <label htmlFor="numbAneu">Number of intracranial aneurysms:</label>
                    <input className="input-control" name="numbAneu" value={state.numbAneu} type="number"
                           min="0" max="99"
                           placeholder="Number of intracranial aneurysms"
                           onKeyPress={handleKeyPressed} onChange={onInputChange}/>
                </div>
                <div className="form-group">
                    <label>Shape:</label>
                    <label className="radio">
                        <input name="shape" value="unknown" type="radio" checked={state.shape === "unknown"}
                               onChange={onInputChange}/>Unknown
                    </label>
                    <label className="radio">
                        <input name="shape" value="saccular" type="radio" checked={state.shape === "saccular"}
                               onChange={onInputChange}/>Saccular
                    </label>
                    <label className="radio">
                        <input name="shape" value="saccular-bb" type="radio"
                               checked={state.shape === "saccular-bb"}
                               onChange={onInputChange}/>Sac. broad-based
                    </label>
                    <label className="radio">
                        <input name="shape" value="irregular" type="radio" checked={state.shape === "irregular"}
                               onChange={onInputChange}/>Irregular
                    </label>
                    <label className="radio">
                        <input name="shape" value="blister" type="radio" checked={state.shape === "blister"}
                               onChange={onInputChange}/>Blister
                    </label></div>

                <div className="form-group">
                    <label htmlFor="vessel"> Harbouring vessel:</label>
                    <select className="input-control" name="vessel" value={state.vessel} onChange={onInputChange} required>
                        <option value="">--</option>
                        <option value="ACoA">Anterior Communicating Artery</option>
                        <option value="PCoA">Posterior Communicating Artery</option>
                        <option value="PerA">Pericallosal Artery</option>
                        <option value="ACA">Anterior Cerebral Artery</option>
                        <option value="MCA">Middle Cerebral Artery</option>
                        <option value="PCA">Posterior Cerebral Artery</option>
                        <option value="BA">Basilar Artery</option>
                        <option value="VA">Vertebral Artery</option>
                        <option value="ICA">Internal Carotid Artery</option>
                        <option value="ED">Extradural</option>
                    </select></div>
                <div className="form-group">
                    <label htmlFor="anomalies"> Anomalies:</label>
                    <select className="input-control" name="anomalies" value={state.anomalies} onChange={onInputChange}>
                        <option value="unknown">Unkown</option>
                        <option value="none">None</option>
                        <option value="ID">Infundibular Dilation</option>
                        <option value="Ect">Ectasia</option>
                        <option value="Dol">Dolicho-Basilaris</option>
                        <option value="FPCA">Fetal-type Posterior Cerebral Artery</option>
                        <option value="VAD">Vertebral Artery Dissection</option>
                        <option value="DV">Duplicated Vessel</option>
                        <option value="Sten">Stenosis</option>
                        <option value="AS">Arteriosclerosis</option>
                        <option value="H">Hypoplastic Vessel</option>
                        <option value="C">Collateral Vessel</option>
                        <option value="Elon">Elongation</option>
                        <option value="Tort">Tortuosity</option>
                        <option value="FMD">Fibromuscular Dysplasia</option>
                        <option value="other">Other</option>
                    </select></div>
            </div>

        </div>
        <span data-tip data-for="calc-tooltip" data-tip-disable={state.vessel !== ''}>
        <button id="calc-btn" className="button" type="button" disabled={state.vessel === ''}
                onClick={postToApi}> {loading ? (<SpinnerCircular color={"white"} size={'1.4%'} speed={200} thickness={200}/>) : (<span>Calculate rupture probability </span>)}
        </button>
        </span>

        <ReactTooltip id="calc-tooltip" type='error' place="top" offset={{top: 20}} effect="solid">
            'Harbouring vessel' must not be empty!
        </ReactTooltip>
    </div>);
};


InputForm.propTypes = {
    change: PropTypes.func.isRequired
};

export default InputForm;
